import { Head, usePage } from "@inertiajs/react"
import { AccountShell } from "@/components/AccountShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { formatCents } from "@/lib/fetch-json"
import type { PageProps } from "@/types/inertia"

interface PaymentRow {
  id: number
  invoice_number: string
  description: string
  amount_cents: number
  paid_at: string
}

type PaymentsProps = PageProps<{ payments: PaymentRow[] }>

export default function PaymentsIndex() {
  const { props } = usePage<PaymentsProps>()
  const { payments } = props

  return (
    <>
      <Head title="Payments">
        <meta name="description" content="Your Flywrite payment history and downloadable invoices." />
        <meta property="og:title" content="Payments" />
        <meta property="og:description" content="Your Flywrite payment history and downloadable invoices." />
      </Head>
      <AccountShell>
        <PageHeader
          title="Payments"
          description="Every charge on your account, with downloadable PDF invoices."
        />

        {payments.length === 0 ? (
          <p className="mt-8 text-sm text-ink-muted">No payments yet.</p>
        ) : (
          <div className="mt-8 overflow-x-auto rounded-lg border border-hairline">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline bg-surface text-left">
                  <th className="px-4 py-3 font-medium text-ink-muted">Date</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Description</th>
                  <th className="px-4 py-3 text-right font-medium text-ink-muted">Amount</th>
                  <th className="px-4 py-3 font-medium text-ink-muted">Status</th>
                  <th className="px-4 py-3 text-right font-medium text-ink-muted">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment) => (
                  <tr key={payment.id} className="border-b border-hairline last:border-0">
                    <td className="px-4 py-3 whitespace-nowrap">{payment.paid_at}</td>
                    <td className="px-4 py-3">{payment.description}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {formatCents(payment.amount_cents)}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone="accent">Paid</Badge>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {/* plain link — file download, not an Inertia visit */}
                      <a href={`/account/payments/${payment.id}/invoice`}>Download PDF</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AccountShell>
    </>
  )
}
